/**
 * Session-only franchise location peek for network HQ operators (e.g. Big Frog
 * super admins). The HQ location menu no longer sets this — choosing a store
 * opens that store's account. Kept so a leftover filter can be cleared and
 * so in-flight requests still honor a peek if one is present.
 */

const STORAGE_KEY = "riverr.networkViewLocationId";
/** Keep inline — this helper is imported by ajax.js; avoid utils circular deps. */
const BIG_FROG_ENTERPRISE_ID = "6duhvdsbbxEp2BgNm90f";

export const isBigFrogChildEnterprise = (enterprise = {}) =>
  enterprise?.parentEnterpriseId === BIG_FROG_ENTERPRISE_ID;

export const getNetworkViewLocationId = () => {
  try {
    if (typeof sessionStorage === "undefined") return null;
    const value = sessionStorage.getItem(STORAGE_KEY);
    if (!value || value === "__all__") return null;
    return value;
  } catch (_) {
    return null;
  }
};

export const setNetworkViewLocationId = (locationId) => {
  try {
    if (typeof sessionStorage === "undefined") return;
    if (!locationId || locationId === "__all__") {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, String(locationId));
  } catch (_) {
    /* ignore */
  }
};

export const clearNetworkViewLocationId = () => {
  setNetworkViewLocationId(null);
};

/**
 * Network parent HQ switchers get an "HQ" home + optional franchise peek.
 * Franchise child accounts and non-network tenants keep the warehouse switcher.
 */
export const isNetworkHqViewSwitcher = (
  enterprise = {},
  canSwitchAll = false,
  currentUser = {}
) => {
  if (!canSwitchAll || enterprise?.parentEnterpriseId) return false;
  const entId =
    enterprise?.id ||
    enterprise?.enterpriseId ||
    currentUser?.enterpriseId ||
    "";
  return (
    enterprise?.paymentSplitModel === "network" ||
    entId === BIG_FROG_ENTERPRISE_ID
  );
};

const knownTenant = (enterprise, user) =>
  !!(
    enterprise?.id ||
    enterprise?.enterpriseId ||
    enterprise?.paymentSplitModel ||
    user?.enterpriseId
  );

/**
 * A saved franchise peek hides the HQ shop list only on a network parent.
 * On any other tenant the same session value would blank My stores and
 * leave Seller Stores, which does not read it, looking fine.
 * Before the tenant is known, keep hiding so a real peek does not flash
 * the parent shops.
 */
export const peekHidesOwnedStores = ({
  networkViewLocationId = null,
  enterprise = null,
  user = null,
} = {}) => {
  if (!networkViewLocationId) return false;
  if (!knownTenant(enterprise, user)) return true;
  return isNetworkHqViewSwitcher(enterprise || {}, true, user || {});
};

/** Drop a peek that does not belong on this tenant. */
export const clearStaleNetworkViewPeek = (enterprise, user) => {
  if (!knownTenant(enterprise, user)) return false;
  if (isNetworkHqViewSwitcher(enterprise || {}, true, user || {})) return false;
  if (!getNetworkViewLocationId()) return false;
  clearNetworkViewLocationId();
  return true;
};
